import React, { useEffect, useState, useCallback } from "react";
import { Bell, Check, CheckCheck, MailOpen, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function NotificationBell() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchCount = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.count || 0);
    } catch { /* silent */ }
  }, [user]);

  const fetchList = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications", { params: { limit: 20 } });
      setItems(data);
    } catch {
      setItems([]);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => clearInterval(t);
  }, [user, fetchCount]);

  const onOpenChange = (v) => {
    setOpen(v);
    if (v) fetchList();
  };

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    setItems(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnread(u => Math.max(0, u - 1));
  };

  const markUnread = async (id) => {
    await api.post(`/notifications/${id}/unread`);
    setItems(prev => prev.map(n => n.id === id ? { ...n, read: false } : n));
    setUnread(u => u + 1);
  };

  const deleteNotif = async (id) => {
    await api.delete(`/notifications/${id}`);
    const wasUnread = items.find(n => n.id === id)?.read === false;
    setItems(prev => prev.filter(n => n.id !== id));
    if (wasUnread) setUnread(u => Math.max(0, u - 1));
  };

  const deleteAll = async () => {
    await api.delete("/notifications");
    setItems([]);
    setUnread(0);
  };

  const markAll = async () => {
    await api.post("/notifications/mark-all-read");
    setItems(prev => prev.map(n => ({ ...n, read: true })));
    setUnread(0);
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          data-testid="notifications-bell"
          className="relative w-9 h-9 rounded-full border border-border bg-card hover:bg-muted transition-colors flex items-center justify-center"
          aria-label="Notifications"
        >
          <Bell size={16} />
          {unread > 0 && (
            <span
              data-testid="notifications-unread-count"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E97A48] text-white text-[10px] font-bold flex items-center justify-center"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 max-h-[400px] overflow-hidden flex flex-col p-0"
        data-testid="notifications-panel"
      >
        <div className="flex items-center justify-between p-3 border-b border-border">
          <div className="font-display font-semibold tracking-tight">Notifications</div>
          {unread > 0 && (
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={markAll}
                data-testid="notifications-mark-all"
                className="text-xs h-7"
              >
                <CheckCheck size={12} className="mr-1" /> Mark all read
              </Button>
              <Button variant="ghost" size="sm" onClick={deleteAll}
                className="text-xs h-7 text-destructive hover:text-destructive hover:bg-destructive/10">
                <Trash2 size={12} className="mr-1" /> Clear all
              </Button>
            </div>
          )}
        </div>
        <div className="overflow-y-auto flex-1">
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">No notifications yet.</div>
          ) : (
            items.map(n => (
              <NotificationItem key={n.id} n={n} onRead={markRead} onUnread={markUnread} onDelete={deleteNotif} onClose={() => setOpen(false)} />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function NotificationItem({ n, onRead, onUnread, onDelete, onClose }) {
  const { user } = useAuth();
  const isUnread = !n.read;

  let to = null;
  if (n.type && n.type.startsWith("negotiation_")) {
    to = user?.role === "owner" ? "/owner/negotiations" : "/account/negotiations";
  } else if (["booking_confirmed","booking_declined","booking_cancelled","handover_confirmed","booking_paid"].includes(n.type)) {
    to = user?.role === "owner" ? "/owner/bookings" : "/account/bookings";
  } else if (n.type === "review_received") {
    to = "/owner/cars";
  }

  const handleClick = () => {
    if (isUnread) onRead(n.id);
    onClose();
  };

  const Wrapper = to ? Link : "div";
  const wrapperProps = to ? { to, onClick: handleClick } : { onClick: isUnread ? () => onRead(n.id) : undefined };

  return (
    <Wrapper
      {...wrapperProps}
      data-testid={`notif-${n.id}`}
      className={`group block px-3 py-2.5 border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors cursor-pointer ${isUnread ? "bg-[#FCE9DC]/30" : ""}`}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className={`text-sm ${isUnread ? "font-semibold" : ""}`}>{n.title}</div>
          {n.body && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</div>}
          <div className="text-[10px] text-muted-foreground mt-1">
            {n.created_at ? new Date(n.created_at).toLocaleString() : ""}
          </div>
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          {isUnread ? (
            <>
              <button
                type="button"
                onClick={e => { e.preventDefault(); e.stopPropagation(); onRead(n.id); }}
                className="text-[#2A9D8F] hover:bg-[#D0F5F2] rounded-full w-6 h-6 flex items-center justify-center"
                data-testid={`notif-read-${n.id}`}
                aria-label="Mark read"
              >
                <Check size={12} />
              </button>
              <button type="button" onClick={e => { e.preventDefault(); e.stopPropagation(); onDelete(n.id); }}
                className="p-1 rounded hover:bg-destructive/10 text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Delete notification">
                <Trash2 size={12} />
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={e => { e.preventDefault(); e.stopPropagation(); onUnread(n.id); }}
                className="text-muted-foreground hover:bg-muted rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`notif-unread-${n.id}`}
                aria-label="Mark unread"
              >
                <MailOpen size={12} />
              </button>
              <button type="button" onClick={e => { e.preventDefault(); e.stopPropagation(); onDelete(n.id); }}
                className="p-1 rounded hover:bg-destructive/10 text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Delete notification">
                <Trash2 size={12} />
              </button>
            </>
          )}
        </div>
      </div>
    </Wrapper>
  );
}
